import { IS_LIVE_TRANSCRIPTS_ENABLED } from '@/features/flags';
import { useIsDesktopTranscriptionServiceV2ReadSideEnabled } from '@/hooks/desktop/useIsDesktopTranscriptionServiceV2ReadSideEnabled';
import { useCurrentSessionIsTabSharing } from '@/hooks/useCurrentSessionIsTabSharing';
import { gotFirstNonEmptyTranscriptChunk } from '@/lib/analytics/scribe/general';
import { isSessionNotFoundError } from '@/lib/SessionRecordingProvider/errors';
import { inheritBlankSpeakerIds } from '@/lib/SessionRecordingProvider/inherit-blank-speaker-ids';
import { getLiveTranscript } from '@/pages/api/scribeAPIV2';
import { type RootState, store } from '@/store';
import {
  type LiveTranscriptions,
  type RecordingChunk,
  type TranscriptLiveResponse,
} from '@/types';
import { isOnPrem } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  hasInFlightChunkUploadsForSession,
  useSessionRecordingStore,
} from '@heidi/capture';
import { TranscriptInUse, TranscriptionFeedbackStatus } from '@heidi/schema/ml-scribe';
import {
  GET_LIVE_TRANSCRIPT_BY_SESSION_ID,
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import {
  mergeChunkResponsePreviews,
  notFoundSessionIds,
  useChunkResponsePreviews,
} from '@heidi/session/data';
import { useQuery } from '@tanstack/react-query';
import { round, values } from 'lodash-es';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentUser } from './current-user-cache';
import {
  shouldRetryUnlessSessionNotFound,
  useQueryT11nTranscriptBySessionId,
} from './useQueryT11nTranscriptBySessionId';

// paths

/**
 * Prefer the session-cache upload progress used by note-gen Loading UI
 * (`chunkUploadProgress` on GetSessionById). Falls back to this session's
 * ChunkRecorder drain state for the brief window after stop before note-gen
 * seeds progress — never global `processingChunksIds`, which can stick across
 * hydrated sessions.
 */
export function isLiveChunkUploadInProgress(sessionId: string): boolean {
  const session = reactQueryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId },
  ]);
  const progress = session?.chunkUploadProgress;
  if (progress !== undefined && progress < 100) {
    return true;
  }

  const recordingState = useSessionRecordingStore.getState();
  if (recordingState.sessionId === sessionId) {
    // The v2 slot covers live capture before stopCaptureV2 retires it.
    if (recordingState.captureSlot) {
      return true;
    }
    if (
      values(recordingState.chunkRecorders).some(
        (recorder) => recorder.hasChunksInLimbo() || !recorder.doneRecording
      )
    ) {
      return true;
    }
  }

  // Detached v2 uploads outlive the store's capture slot and may outlive its
  // current-session pointer. The registry reader is session-scoped.
  return hasInFlightChunkUploadsForSession(sessionId);
}

export function resolveLiveTranscriptStatus(
  sessionId: string
): 'complete' | 'in_progress' {
  if (store.getState().timerModel.isRunning) {
    return 'in_progress';
  }
  // Cloud keeps timer-only status. The chunk-drain gate is on-prem only
  // (Whisper / no t11n service makes the complete race much more costly).
  if (isOnPrem && isLiveChunkUploadInProgress(sessionId)) {
    return 'in_progress';
  }
  return 'complete';
}

/** Seed / update session chunk upload progress so live transcript status and
 * Loading UI share one source of truth. */
export function setSessionChunkUploadProgress(
  sessionId: string,
  progress: number | undefined
) {
  reactQueryClient.setQueryData<QuerySessionResponse>(
    [GET_SESSION_BY_ID, { sessionId }],
    (oldData) => {
      if (!oldData) {
        return undefined;
      }
      return {
        ...oldData,
        chunkUploadProgress: progress,
      };
    }
  );
}

/** Fetch live transcript. Throws on 404 without tripping the circuit breaker — that is
 * owned by the onError callback from useOnError (wired via SessionRecordingProvider's config),
 * which ensures onInterrupt() fires before the circuit breaker is set. */
async function fetchLiveTranscript(
  sessionId: string,
  status?: 'complete' | 'in_progress'
) {
  const response = await getLiveTranscript(sessionId, status);
  return response.data.response;
}

export const preFetchLiveTranscripts = async (sessionId: string) => {
  await reactQueryClient.prefetchQuery({
    queryKey: [GET_LIVE_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
    queryFn: async () => {
      const status = resolveLiveTranscriptStatus(sessionId);
      return fetchLiveTranscript(sessionId, status);
    },
  });
};

export async function ensureLiveTranscript(sessionId: string) {
  return reactQueryClient.ensureQueryData({
    queryKey: [GET_LIVE_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
    queryFn: () => fetchLiveTranscript(sessionId),
  });
}

export async function invalidateLiveTranscript(sessionId: string) {
  if (notFoundSessionIds.has(sessionId)) return;

  try {
    await reactQueryClient.invalidateQueries({
      queryKey: [GET_LIVE_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
    });
  } catch (error) {
    logger.error('Failed to invalidate get live transcript:', error);
  }
}

export function setIsPollingChunks(sessionId: string) {
  reactQueryClient.setQueryData<QuerySessionLiveTranscripts>(
    [GET_LIVE_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
    (oldData) => {
      if (!oldData) {
        return undefined;
      }

      const newData: QuerySessionLiveTranscripts = {
        ...oldData,
        isPolling: true,
      };
      return newData;
    }
  );
}

function handleEnsureTranscriptError(error: unknown, sessionId: string) {
  if (isSessionNotFoundError(error)) {
    const onError = useSessionRecordingStore.getState().config?.onError;
    if (onError) {
      onError(error);
    } else {
      if (sessionId) notFoundSessionIds.add(sessionId);
      logger.error('Session not found but no onError handler configured:', error);
    }
  } else {
    logger.warn('Failed to ensure live transcript:', error);
  }
}

export function pollLiveTranscripts(sessionId: string, interval = 5000, multiple = 3) {
  if (notFoundSessionIds.has(sessionId)) return;

  ensureLiveTranscript(sessionId).catch((error) =>
    handleEnsureTranscriptError(error, sessionId)
  );
  void setIsPollingChunks(sessionId);

  const poll = (attempt = 1) => {
    if (attempt > multiple) return;
    if (notFoundSessionIds.has(sessionId)) return;

    void invalidateLiveTranscript(sessionId);

    setTimeout(() => poll(attempt + 1), interval);
  };

  poll();
}

export function pollLiveTranscriptsWithBackoff(
  sessionId: string,
  initialInterval = 5000,
  maxDuration = 60000
) {
  if (notFoundSessionIds.has(sessionId)) return;

  ensureLiveTranscript(sessionId).catch((error) =>
    handleEnsureTranscriptError(error, sessionId)
  );
  const startTime = Date.now();

  const poll = (interval: number) => {
    if (Date.now() - startTime >= maxDuration) return;
    if (notFoundSessionIds.has(sessionId)) return;

    void invalidateLiveTranscript(sessionId);

    const nextInterval = Math.min(interval * 2, maxDuration - (Date.now() - startTime));
    setTimeout(() => poll(nextInterval), nextInterval);
  };

  poll(initialInterval);
}

export const addChunkToLiveTranscripts = (input: {
  chunkId: number;
  sessionId: string;
  duration: number;
}) => {
  try {
    reactQueryClient.setQueryData<QuerySessionLiveTranscripts>(
      [GET_LIVE_TRANSCRIPT_BY_SESSION_ID, { sessionId: input.sessionId }],
      (oldData) => {
        if (!oldData) {
          return undefined;
        }
        if (!oldData.live) return oldData;
        const lastLiveIndex = oldData.live.length - 1;
        if (lastLiveIndex < 0) return oldData;
        if (!oldData.live[lastLiveIndex]?.chunks) return oldData;
        const chunk = {
          id: input.chunkId,
          status: TranscriptionFeedbackStatus.IN_PROGRESS,
          transcript: '',
          duration: input.duration,
        };
        const newLive = [...oldData.live];
        if (!newLive[lastLiveIndex]) {
          return oldData;
        }
        newLive[lastLiveIndex].chunks = [...oldData.live[lastLiveIndex].chunks, chunk];
        return {
          ...oldData,
          live: newLive,
        };
      }
    );
  } catch (error) {
    logger.error('Failed to add chunk to live transcripts:', error);
  }
};

export type QuerySessionLiveTranscripts = LiveTranscriptions & {
  isPolling?: boolean;
};

const addTimestampsToRecordings = (recordings: RecordingChunk[] | undefined) => {
  let cumulativeTimestamp = 0;

  return recordings?.map((recording) => {
    const newChunks = recording.chunks
      .map((chunk) => {
        const timestamp = cumulativeTimestamp;
        cumulativeTimestamp += chunk.duration;
        return { ...chunk, timestamp };
      })
      .filter((c) => c.transcript && c.transcript.length > 0);

    return { ...recording, chunks: newChunks };
  });
};

const hasSeenTranscriptForSession: Set<string> = new Set();

const transcriptIsNotEmpty = (transcript: TranscriptLiveResponse['response']['live']) => {
  try {
    return Boolean(
      transcript?.some((r) => r.chunks.some((c) => c.transcript?.trim()?.length))
    );
  } catch {
    return false;
  }
};

export function useQueryLiveTranscriptBySessionId(
  sessionId: string,
  {
    diarise = false,
    displayChunkPreviews = false,
  }: { diarise?: boolean; displayChunkPreviews?: boolean } = {}
) {
  const isTabSharingSession = useCurrentSessionIsTabSharing();
  const hasStartedTimer = useSelector((s: RootState) => (s.timerModel?.seconds ?? 0) > 0);
  const isTimerRunning = useSelector((s: RootState) => s.timerModel?.isRunning ?? false);
  const isDesktopTranscriptionServiceV2ReadSideEnabled =
    useIsDesktopTranscriptionServiceV2ReadSideEnabled();

  const isQueryEnabled =
    !!sessionId &&
    !notFoundSessionIds.has(sessionId) &&
    // `hasStartedTimer` means a recording is live in this tab right now
    // (scribeRecordModel -> timerModel.onStart). On-prem transcripts are
    // Whisper-transcribed server-side and viewed outside an active recording
    // (uploaded audio, or a completed recording re-opened), so the timer isn't
    // running then — gate on isOnPrem so the query still fires.
    (((hasStartedTimer || isOnPrem) && IS_LIVE_TRANSCRIPTS_ENABLED()) ||
      // Uploaded audio is only on this ml-scribe endpoint. The desktop v2
      // read side still needs the query when the timer never started; the
      // t11n query below never sees uploads. ml-scribe omits the external
      // transcript, so the merge does not duplicate the recording.
      isDesktopTranscriptionServiceV2ReadSideEnabled);

  const result = useQuery<QuerySessionLiveTranscripts>({
    queryKey: [GET_LIVE_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
    enabled: isQueryEnabled,
    retry: shouldRetryUnlessSessionNotFound,
    queryFn: async () => {
      const status = resolveLiveTranscriptStatus(sessionId);
      return fetchLiveTranscript(sessionId, status);
    },
  });

  useEffect(() => {
    if (result.error && isSessionNotFoundError(result.error)) {
      const onError = useSessionRecordingStore.getState().config?.onError;
      if (onError) {
        onError(result.error);
      } else {
        logger.error(
          'Session not found in live transcript query but no onError handler configured:',
          result.error
        );
      }
    }
  }, [result.error, sessionId]);

  let backup = addTimestampsToRecordings(result.data?.backup);
  let live = addTimestampsToRecordings(result.data?.live);
  const user_uploaded = addTimestampsToRecordings(result.data?.user_uploaded);

  // On the desktop transcription-service v2 read-side the ml-scribe query above
  // runs too (uploaded audio); the t11n query supplies the diarised recording
  // transcript.
  const t11nResult = useQueryT11nTranscriptBySessionId(sessionId, {
    // The T11n path hits the standalone transcription service
    // (NEXT_PUBLIC_TRANSCRIPTION_SERVICE_BASE_URL), which on-prem doesn't run —
    // on-prem gets its transcript via the ml-scribe live endpoint above.
    enabled: isQueryEnabled && !isOnPrem,
    diarise,
    // Mirrors the cloud rule in `resolveLiveTranscriptStatus`: the timer is
    // the recording-in-progress signal, so a stopped timer is a settled read.
    settled: !isTimerRunning,
  });

  if (
    !hasSeenTranscriptForSession.has(sessionId) &&
    (transcriptIsNotEmpty(result.data?.live) || transcriptIsNotEmpty(result.data?.backup))
  ) {
    gotFirstNonEmptyTranscriptChunk({
      sessionId,
      isTabSharing: isTabSharingSession,
    });
    hasSeenTranscriptForSession.add(sessionId);
  }

  const previews = useChunkResponsePreviews(
    displayChunkPreviews ? getCurrentUser()?.kinde_user_id : undefined,
    sessionId
  );
  const displayTranscript = displayChunkPreviews
    ? mergeChunkResponsePreviews(
        t11nResult.data ?? { session_id: sessionId, recordings: [] },
        previews
      )
    : t11nResult.data;
  const t11nTransformed = displayTranscript?.recordings
    ?.map((recording) => ({
      id: recording.recording_id,
      created_at: recording.start_date,
      starttime: recording.start_date ?? new Date().toISOString(),
      chunks: inheritBlankSpeakerIds(
        recording.chunks.map((chunk) => ({
          timestamp: round(chunk.timestamp_cumulative_ms / 1000),
          id: chunk.index,
          status: TranscriptionFeedbackStatus.COMPLETED,
          transcript: chunk.text,
          duration: round((chunk.audio_info.duration ?? 0) / 1000),
          diarisedTranscript: diarise
            ? (chunk.diarised_transcript ?? undefined)
            : undefined,
          diarisationStatus: chunk.diarisation_status ?? undefined,
        }))
      ),
    }))
    .flat();

  // Whichever transcript we use, always merge with the t11n transcript.
  // ISO 8601 strings sort chronologically by lexicographic comparison.
  live = [...(live ?? []), ...(t11nTransformed ?? [])].sort((a, b) =>
    a.starttime.localeCompare(b.starttime)
  );
  backup = [...(backup ?? []), ...(t11nTransformed ?? [])].sort((a, b) =>
    a.starttime.localeCompare(b.starttime)
  );

  const hasT11nTranscripts = displayTranscript?.recordings?.length;

  return {
    // The t11n query is disabled on-prem; a disabled react-query stays
    // status:'pending' forever, so exclude it there or isPending never clears.
    isPending:
      displayChunkPreviews && hasT11nTranscripts
        ? false
        : result.isPending || (!isOnPrem && t11nResult.isPending),
    data: {
      ...(result.data ?? {}),
      live,
      backup,
      user_uploaded,
      transcript_in_use: hasT11nTranscripts
        ? TranscriptInUse.live
        : result.data?.transcript_in_use,
    },
  };
}

// Cache identities are single-site in `@heidi/session/contracts` (ARCFE-1001).
// These were DOUBLE-DECLARED here — the literals agreed, but two independent
// declarations of one cache key is an unguarded drift risk: a change to either
// side splits writes and reads across two caches. Imported (not just
// re-exported) because this module uses them locally too.
export { TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY } from './useQueryT11nTranscriptBySessionId';
export { GET_LIVE_TRANSCRIPT_BY_SESSION_ID };

// `areAllTranscriptsEmpty` moved to `@heidi/session/data` with the rest of the
// transcript-deleted detection, which is its only production consumer. The
// re-export keeps this path resolving for existing importers; it is a
// re-export, not a declaration.
export { areAllTranscriptsEmpty } from '@heidi/session/data';
